import React, { useState, useEffect, useRef } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { TowerEquipment } from '../../types/site';
import { useSiteStore } from '../../store/siteStore';
import { RotateCcw, ChevronDown, ChevronRight, Plus, Trash2, Search, Settings, ArrowLeft } from 'lucide-react';
import { Equipment } from '../../types';
import { radioEquipmentDatabase } from '../../data/radioEquipment';

interface EquipmentTooltipProps {
  equipment: TowerEquipment;
  screenPosition: { x: number; y: number } | null;
  onClose: () => void;
}

const EquipmentTooltip: React.FC<EquipmentTooltipProps> = ({ equipment, screenPosition, onClose }) => {
  const { currentSite, updateEquipment, moveEquipmentToHeight, removeEquipment, setSelectedEquipment } = useSiteStore();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [azimuth, setAzimuth] = useState(equipment.azimuth);
  const [elevation, setElevation] = useState(equipment.elevation);
  const [label, setLabel] = useState(equipment.label || '');
  const [offset, setOffset] = useState(equipment.offset !== undefined ? equipment.offset : 0.1);
  const [desiredHeight, setDesiredHeight] = useState<number>(0);
  const [originalHeight, setOriginalHeight] = useState<number>(0); // Track original height for reset
  
  // Collapsible section states (all collapsed by default)
  const [expandedSections, setExpandedSections] = useState({
    mounting: false,
    label: false,
    radio: false,
    
    azimuth: false,
    elevation: false,
    offset: false
  });

  const [radioSearchTerm, setRadioSearchTerm] = useState('');
  const [showRadioList, setShowRadioList] = useState(false);
  const [showRadioConfig, setShowRadioConfig] = useState(false);

  // Radio config states - initialize from equipment
  const [radioConfig, setRadioConfig] = useState({
    ipAddress: equipment.radioConfig?.ipAddress || '',
    frequency: equipment.radioConfig?.frequency || '',
    vlan: equipment.radioConfig?.vlan || '',
    ssid: equipment.radioConfig?.ssid || ''
  });

  // Radio config section states
  const [radioConfigSections, setRadioConfigSections] = useState({
    ipAddress: false,
    frequency: false,
    vlan: false,
    ssid: false
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleRadioConfigSection = (section: keyof typeof radioConfigSections) => {
    setRadioConfigSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleAddRadio = (radio: Equipment) => {
    updateEquipment(equipment.id, { radio });
    setShowRadioList(false);
    setRadioSearchTerm('');
  };

  const handleRemoveRadio = () => {
    updateEquipment(equipment.id, { radio: undefined });
  };

  const filteredRadios = radioEquipmentDatabase.filter(radio =>
    radio.name.toLowerCase().includes(radioSearchTerm.toLowerCase()) ||
    radio.manufacturer.toLowerCase().includes(radioSearchTerm.toLowerCase())
  );

  useEffect(() => {
    setAzimuth(equipment.azimuth);
    setElevation(equipment.elevation);
    setLabel(equipment.label || '');
    
    setOffset(equipment.offset !== undefined ? equipment.offset : 0.1);
    setRadioConfig({
      ipAddress: equipment.radioConfig?.ipAddress || '',
      frequency: equipment.radioConfig?.frequency || '',
      vlan: equipment.radioConfig?.vlan || '',
      ssid: equipment.radioConfig?.ssid || ''
    });
    // Initialize desiredHeight to current mounting point height so arrows start there
    const mpCurrent = useSiteStore.getState().currentSite?.tower.mountingPoints.find(m => m.id === equipment.mountingPointId);
    const currentHeight = mpCurrent ? mpCurrent.height : 0;
    setDesiredHeight(currentHeight);
    setOriginalHeight(currentHeight); // Store original height for reset button
  }, [equipment]);

  if (!currentSite) return null;
  
  // If screenPosition is not available yet, use a default position (center of screen)
  const defaultPosition = screenPosition || { x: window.innerWidth / 2, y: window.innerHeight / 2 };

  const handleAzimuthChange = (value: number) => {
    const normalizedAzimuth = ((value % 360) + 360) % 360;
    setAzimuth(normalizedAzimuth);
    updateEquipment(equipment.id, { azimuth: normalizedAzimuth });
  };

  const handleElevationChange = (value: number) => {
    const clampedElevation = Math.max(-90, Math.min(90, value));
    setElevation(clampedElevation);
    updateEquipment(equipment.id, { elevation: clampedElevation });
  };

  const handleResetAzimuth = () => {
    setAzimuth(0);
    updateEquipment(equipment.id, { azimuth: 0 });
  };

  const handleResetElevation = () => {
    setElevation(0);
    updateEquipment(equipment.id, { elevation: 0 });
  };

  const handleLabelChange = (value: string) => {
    setLabel(value);
    updateEquipment(equipment.id, { label: value || undefined });
  };

  

  const handleOffsetChange = (value: number) => {
    const clampedOffset = Math.max(0, Math.min(2.0, value));
    setOffset(clampedOffset);
    updateEquipment(equipment.id, { offset: clampedOffset });
  };

  const handleResetOffset = () => {
    setOffset(0.1);
    updateEquipment(equipment.id, { offset: 0.1 });
  };

  const azimuthDirection = (az: number): string => {
    if (az >= 0 && az < 45) return 'N';
    if (az >= 45 && az < 135) return 'E';
    if (az >= 135 && az < 225) return 'S';
    if (az >= 225 && az < 315) return 'W';
    return 'N';
  };

  const mp = currentSite.tower.mountingPoints.find(m => m.id === equipment.mountingPointId);
  const isDish = equipment.equipment.type === 'radio-dish';

  const towerHeight = currentSite.tower.height;

  return (
    <>
    <div
      className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200"
      style={{
        left: `${defaultPosition.x + 20}px`,
        top: `${defaultPosition.y - 100}px`,
        transform: defaultPosition.x > window.innerWidth - 300 ? 'translateX(-100%)' : 'none',
        overflow: 'hidden',
        width: '296px'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-2 px-3 pt-3">
        {showRadioConfig && (
          <button
            onClick={() => setShowRadioConfig(false)}
            className="flex items-center space-x-1 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft size={14} />
            <span className="text-xs">Back</span>
          </button>
        )}
        <h3 className="text-sm font-semibold text-gray-800">
          {showRadioConfig ? 'Radio Configuration' : equipment.equipment.name}
        </h3>
        <div className="flex items-center space-x-1">
          {!showRadioConfig && (
            <button
              type="button"
              onClick={() => setShowConfirmDelete(true)}
              className="p-1 hover:bg-red-50 rounded text-red-500 hover:text-red-600"
              title="Remove equipment"
            >
              <Trash2 size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
            title="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M12.854 4.854a.5.5 0 0 0-.708-.708L8 7.293 4.854 4.146a.5.5 0 1 0-.708.708L7.293 8l-3.147 3.146a.5.5 0 0 0 .708.708L8 8.707l3.146 3.147a.5.0 0 0 0 .708-.708L8.707 8l3.147-3.146z"/>
            </svg>
          </button>
        </div>
      </div>

        <div 
          className="transition-transform duration-300 ease-in-out flex overflow-hidden"
          style={{ 
            transform: showRadioConfig ? 'translateX(-296px)' : 'translateX(0)',
            width: '592px'
          }}
        >
          {/* Main Sections */}
          <div className="space-y-0.5 flex-shrink-0 pr-3" style={{ width: '296px' }}>
        {/* Label */}
        <div className="overflow-hidden">
          <button
            onClick={() => toggleSection('label')}
            className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-50 transition-colors rounded"
          >
            <div className="flex items-center space-x-2">
              <div className={`transition-transform duration-200 ${expandedSections.label ? 'rotate-0' : '-rotate-90'}`}>
                <ChevronDown size={14} />
              </div>
              <label className="text-xs font-normal text-gray-600">Label</label>
            </div>
            {label && <span className="text-xs text-gray-500 truncate max-w-[100px]">{label}</span>}
          </button>
          
          <div 
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              expandedSections.label ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-3 pb-3 space-y-2">
              <input
                type="text"
                value={label}
                onChange={(e) => handleLabelChange(e.target.value)}
                placeholder="Custom label (optional)"
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              />
              <p className="text-[10px] text-gray-500">
                Leave empty to show equipment specs
              </p>
            </div>
          </div>
        </div>

        {/* Radio */}
        <div className="overflow-hidden">
          <button
            onClick={() => toggleSection('radio')}
            className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-50 transition-colors rounded"
          >
            <div className="flex items-center space-x-2">
              <div className={`transition-transform duration-200 ${expandedSections.radio ? 'rotate-0' : '-rotate-90'}`}>
                <ChevronDown size={14} />
              </div>
              <label className="text-xs font-normal text-gray-600">Radio</label>
            </div>
            {equipment.radio && (
              <span className="text-xs text-gray-500 truncate max-w-[120px]">{equipment.radio.name}</span>
            )}
          </button>
          
          <div 
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              expandedSections.radio ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-3 pb-3 space-y-2">
              {equipment.radio ? (
                <div className="space-y-2">
                  <div className="bg-gray-50 p-2 rounded border border-gray-200">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1">
                        <h4 className="text-xs font-semibold text-gray-800">{equipment.radio.name}</h4>
                        <p className="text-[10px] text-gray-500 capitalize">{equipment.radio.manufacturer}</p>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setShowRadioConfig(true)}
                          className="p-1 hover:bg-blue-100 rounded text-blue-600 hover:text-blue-700"
                          title="Configure radio"
                        >
                          <Settings size={12} />
                        </button>
                        <button
                          onClick={handleRemoveRadio}
                          className="p-1 hover:bg-red-100 rounded text-red-500 hover:text-red-700"
                          title="Remove radio"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                    {equipment.radio.specifications && (
                      <div className="text-[10px] text-gray-600 space-y-0.5">
                        {Object.entries(equipment.radio.specifications).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="capitalize">{key}:</span>
                            <span className="font-medium">{value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : !showRadioList ? (
                <button
                  onClick={() => setShowRadioList(true)}
                  className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium transition-colors"
                >
                  <Plus size={14} />
                  <span>Add Radio</span>
                </button>
              ) : (
                <div className="space-y-2">
                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={12} />
                    <input
                      type="text"
                      placeholder="Search radios..."
                      value={radioSearchTerm}
                      onChange={(e) => setRadioSearchTerm(e.target.value)}
                      className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                      autoFocus
                    />
                  </div>
                  
                  {/* Radio List */}
                  <div className="max-h-48 overflow-y-auto space-y-1 border border-gray-200 rounded p-1">
                    {filteredRadios.map((radio) => (
                      <button
                        key={radio.id}
                        onClick={() => handleAddRadio(radio)}
                        className="w-full text-left p-2 hover:bg-blue-50 rounded transition-colors border border-transparent hover:border-blue-300"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-xs font-semibold text-gray-800">{radio.name}</h4>
                            <p className="text-[10px] text-gray-500 capitalize">{radio.manufacturer}</p>
                          </div>
                          <span className="text-[10px] font-bold text-blue-600">${radio.price}</span>
                        </div>
                        {radio.specifications && (
                          <div className="text-[10px] text-gray-600 mt-1">
                            {Object.entries(radio.specifications).slice(0, 2).map(([key, value]) => (
                              <span key={key} className="mr-2">
                                {value}
                              </span>
                            ))}
                          </div>
                        )}
                      </button>
                    ))}
                    {filteredRadios.length === 0 && (
                      <div className="text-center text-xs text-gray-500 py-4">
                        No radios found
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowRadioList(false);
                      setRadioSearchTerm('');
                    }}
                    className="w-full px-2 py-1 text-xs text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mounting Height */}
        <div className="overflow-hidden">
          <button
            onClick={() => toggleSection('mounting')}
            className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-50 transition-colors rounded"
          >
            <div className="flex items-center space-x-2">
              <div className={`transition-transform duration-200 ${expandedSections.mounting ? 'rotate-0' : '-rotate-90'}`}>
                <ChevronDown size={14} />
              </div>
              <label className="text-xs font-normal text-gray-600">Mounting Height</label>
            </div>
            <span className="text-xs text-gray-500">{mp ? `${mp.height}m` : ''}</span>
          </button>
          
          <div 
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              expandedSections.mounting ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-3 pb-3 space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min={0}
                  max={towerHeight}
                  step={0.5}
                  value={desiredHeight}
                  onChange={(e) => setDesiredHeight(Number(e.target.value))}
                  placeholder={mp ? `${mp.height}` : '0'}
                  className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={() => {
                    moveEquipmentToHeight(equipment.id, desiredHeight);
                  }}
                  className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                >
                  Move
                </button>
                <button
                  onClick={() => {
                    setDesiredHeight(originalHeight);
                    moveEquipmentToHeight(equipment.id, originalHeight);
                  }}
                  className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
                  title="Reset to original height"
                >
                  <RotateCcw size={12} />
                </button>
              </div>
              <p className="text-[10px] text-gray-500">Snaps to nearest mounting point on the same side.</p>
            </div>
          </div>
        </div>

        

        {/* Azimuth */}
        <div className="overflow-hidden">
          <button
            onClick={() => toggleSection('azimuth')}
            className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-50 transition-colors rounded"
          >
            <div className="flex items-center space-x-2">
              <div className={`transition-transform duration-200 ${expandedSections.azimuth ? 'rotate-0' : '-rotate-90'}`}>
                <ChevronDown size={14} />
              </div>
              <label className="text-xs font-normal text-gray-600">Azimuth</label>
            </div>
            <span className="text-xs text-gray-500">
              {Math.round(azimuth)}° {azimuthDirection(azimuth)}
            </span>
          </button>
          
          <div 
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              expandedSections.azimuth ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-3 pb-3 space-y-2">
              <div className="flex items-center justify-between">
                <input
                  type="number"
                  min="0"
                  max="360"
                  value={Math.round(azimuth)}
                  onChange={(e) => handleAzimuthChange(Number(e.target.value))}
                  className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={handleResetAzimuth}
                  className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
                  title="Reset to 0°"
                >
                  <RotateCcw size={12} />
                </button>
              </div>
              <input
                type="range"
                min="0"
                max="360"
                step="1"
                value={azimuth}
                onChange={(e) => handleAzimuthChange(Number(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
        </div>

        {/* Elevation */}
        <div className="overflow-hidden">
          <button
            onClick={() => toggleSection('elevation')}
            className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-50 transition-colors rounded"
          >
            <div className="flex items-center space-x-2">
              <div className={`transition-transform duration-200 ${expandedSections.elevation ? 'rotate-0' : '-rotate-90'}`}>
                <ChevronDown size={14} />
              </div>
              <label className="text-xs font-normal text-gray-600">Elevation</label>
            </div>
            <span className="text-xs text-gray-500">
              {Math.round(elevation)}°
            </span>
          </button>
          
          <div 
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              expandedSections.elevation ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-3 pb-3 space-y-2">
              <div className="flex items-center justify-between">
                <input
                  type="number"
                  min="-90"
                  max="90"
                  value={Math.round(elevation)}
                  onChange={(e) => handleElevationChange(Number(e.target.value))}
                  className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={handleResetElevation}
                  className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
                  title="Reset to 0°"
                >
                  <RotateCcw size={12} />
                </button>
              </div>
              <input
                type="range"
                min="-90"
                max="90"
                step="1"
                value={elevation}
                onChange={(e) => handleElevationChange(Number(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
        </div>

        {/* Offset Distance */}
        <div className="overflow-hidden">
          <button
            onClick={() => toggleSection('offset')}
            className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-50 transition-colors rounded"
          >
            <div className="flex items-center space-x-2">
              <div className={`transition-transform duration-200 ${expandedSections.offset ? 'rotate-0' : '-rotate-90'}`}>
                <ChevronDown size={14} />
              </div>
              <label className="text-xs font-normal text-gray-600">Mount Offset</label>
            </div>
            <span className="text-xs text-gray-500">
              {(offset * 100).toFixed(0)}cm
            </span>
          </button>
          
          <div 
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              expandedSections.offset ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-3 pb-3 space-y-2">
              <div className="flex items-center justify-end">
                <button
                  onClick={handleResetOffset}
                  className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-gray-700"
                  title="Reset to 10cm"
                >
                  <RotateCcw size={12} />
                </button>
              </div>
              <input
                type="range"
                min="0"
                max="2.0"
                step="0.05"
                value={offset}
                onChange={(e) => handleOffsetChange(Number(e.target.value))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-[10px] text-gray-500">
                <span>0cm (tower)</span>
                <span>200cm</span>
              </div>
              <p className="text-[10px] text-gray-500">
                Distance from mounting point (for long brackets)
              </p>
            </div>
          </div>
        </div>

        {/* Mounting Info */}
        {mp && (
          <div className="text-xs text-gray-600 pt-2 pb-3 px-3 border-t border-gray-200">
            Height: {mp.height}m | Side: {mp.side}
          </div>
        )}
        </div>

        {/* Radio Configuration Panel */}
        <div 
          className="space-y-0.5 flex-shrink-0 pr-3"
          style={{ width: '296px' }}
        >
          {/* IP Address */}
          <div className="overflow-hidden">
            <button
              onClick={() => toggleRadioConfigSection('ipAddress')}
              className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-50 transition-colors rounded"
            >
              <div className="flex items-center space-x-2">
                <div className={`transition-transform duration-200 ${radioConfigSections.ipAddress ? 'rotate-0' : '-rotate-90'}`}>
                  <ChevronDown size={14} />
                </div>
                <label className="text-xs font-normal text-gray-600">IP Address</label>
              </div>
              {radioConfig.ipAddress && (
                <span className="text-xs text-gray-500">{radioConfig.ipAddress}</span>
              )}
            </button>
            
            <div 
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                radioConfigSections.ipAddress ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="px-3 pb-3">
                <input
                  type="text"
                  value={radioConfig.ipAddress}
                  onChange={(e) => {
                    const newConfig = { ...radioConfig, ipAddress: e.target.value };
                    setRadioConfig(newConfig);
                    updateEquipment(equipment.id, { radioConfig: newConfig });
                  }}
                  placeholder="192.168.1.1"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Frequency */}
          <div className="overflow-hidden">
            <button
              onClick={() => toggleRadioConfigSection('frequency')}
              className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-50 transition-colors rounded"
            >
              <div className="flex items-center space-x-2">
                <div className={`transition-transform duration-200 ${radioConfigSections.frequency ? 'rotate-0' : '-rotate-90'}`}>
                  <ChevronDown size={14} />
                </div>
                <label className="text-xs font-normal text-gray-600">Frequency</label>
              </div>
              {radioConfig.frequency && (
                <span className="text-xs text-gray-500">{radioConfig.frequency}</span>
              )}
            </button>
            
            <div 
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                radioConfigSections.frequency ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="px-3 pb-3">
                <input
                  type="text"
                  value={radioConfig.frequency}
                  onChange={(e) => {
                    const newConfig = { ...radioConfig, frequency: e.target.value };
                    setRadioConfig(newConfig);
                    updateEquipment(equipment.id, { radioConfig: newConfig });
                  }}
                  placeholder="5180 MHz"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* VLAN */}
          <div className="overflow-hidden">
            <button
              onClick={() => toggleRadioConfigSection('vlan')}
              className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-50 transition-colors rounded"
            >
              <div className="flex items-center space-x-2">
                <div className={`transition-transform duration-200 ${radioConfigSections.vlan ? 'rotate-0' : '-rotate-90'}`}>
                  <ChevronDown size={14} />
                </div>
                <label className="text-xs font-normal text-gray-600">VLAN</label>
              </div>
              {radioConfig.vlan && (
                <span className="text-xs text-gray-500">{radioConfig.vlan}</span>
              )}
            </button>
            
            <div 
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                radioConfigSections.vlan ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="px-3 pb-3">
                <input
                  type="text"
                  value={radioConfig.vlan}
                  onChange={(e) => {
                    const newConfig = { ...radioConfig, vlan: e.target.value };
                    setRadioConfig(newConfig);
                    updateEquipment(equipment.id, { radioConfig: newConfig });
                  }}
                  placeholder="100"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* SSID */}
          <div className="overflow-hidden">
            <button
              onClick={() => toggleRadioConfigSection('ssid')}
              className="w-full flex items-center justify-between px-2 py-1 hover:bg-gray-50 transition-colors rounded"
            >
              <div className="flex items-center space-x-2">
                <div className={`transition-transform duration-200 ${radioConfigSections.ssid ? 'rotate-0' : '-rotate-90'}`}>
                  <ChevronDown size={14} />
                </div>
                <label className="text-xs font-normal text-gray-600">SSID</label>
              </div>
              {radioConfig.ssid && (
                <span className="text-xs text-gray-500 truncate max-w-[100px]">{radioConfig.ssid}</span>
              )}
            </button>
            
            <div 
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                radioConfigSections.ssid ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="px-3 pb-3">
                <input
                  type="text"
                  value={radioConfig.ssid}
                  onChange={(e) => {
                    const newConfig = { ...radioConfig, ssid: e.target.value };
                    setRadioConfig(newConfig);
                    updateEquipment(equipment.id, { radioConfig: newConfig });
                  }}
                  placeholder="MyNetwork"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Confirm Delete Dialog */}
    {showConfirmDelete && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50" onMouseDown={() => setShowConfirmDelete(false)} />
        <div
          className="relative bg-white w-full max-w-sm mx-4 rounded-lg shadow-2xl border border-gray-200 overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b flex items-center">
            <div className="w-8 h-8 mr-2 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
              <Trash2 size={16} />
            </div>
            <h4 className="text-sm font-semibold text-gray-800">Remove equipment?</h4>
          </div>
          <div className="p-4 text-sm text-gray-600">
            <p className="mb-2">This will remove <span className="font-medium text-gray-900">{equipment.equipment.name}</span> from the tower.</p>
            <p>You can always add it again later.</p>
          </div>
          <div className="p-4 bg-gray-50 flex items-center justify-end space-x-2">
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowConfirmDelete(false); }}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const id = equipment.id;
                const { removeEquipment, setSelectedEquipment, refreshSites, recomputeCollisions } = useSiteStore.getState();
                setSelectedEquipment(null);
                removeEquipment(id);
                setShowConfirmDelete(false);
                // Ensure UI reflects changes immediately
                refreshSites();
                recomputeCollisions();
              }}
              className="px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default EquipmentTooltip;

